import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Plus, Repeat, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatTime, getDayName, toISTDateStr } from '../../lib/utils';

const DAYS = [0, 1, 2, 3, 4, 5, 6];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ScheduleSessions() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [existingSessions, setExistingSessions] = useState([]);
  const [calendarSessions, setCalendarSessions] = useState([]);
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => {
    const d = new Date(); const day = d.getDay();
    const start = new Date(d); start.setDate(d.getDate() - day);
    return start;
  });

  // Today's date (IST) and default time (30 min from now)
  const today = (() => {
    const d = new Date();
    // Convert to IST
    const ist = new Date(d.getTime() + (5.5 * 60 * 60 * 1000) + (d.getTimezoneOffset() * 60 * 1000));
    return toISTDateStr(new Date());
  })();
  const defaultTime = (() => {
    const d = new Date(Date.now() + 30 * 60 * 1000);
    return `${String(d.getHours()).padStart(2, '0')}:${String(Math.ceil(d.getMinutes() / 5) * 5 % 60).padStart(2, '0')}`;
  })();

  // Single session form
  const [singleForm, setSingleForm] = useState({
    title: 'Live Session', instructor_id: '', scheduled_date: today, scheduled_time: defaultTime,
    duration_minutes: 60, location: 'zoom'
  });

  // Recurring form
  const [recurForm, setRecurForm] = useState({
    title: 'Live Session', instructor_id: '', repeat_every: 1, repeat_unit: 'week',
    days_of_week: [], start_date: today, start_time: defaultTime, end_type: 'occurrences',
    end_date: '', occurrences: 4, duration_minutes: 60, location: 'zoom'
  });

  useEffect(() => {
    api.get(`/api/batches/${batchId}`).then(res => {
      setBatch(res.data);
      // Don't auto-fill instructor_id here — wait for instructor list to validate
    });
    // Fetch instructors + superadmin (superadmin can substitute for any instructor on leave).
    // Regular admins are intentionally excluded — they can START a class on
    // behalf via the admin-start endpoint, but they aren't assignable AS the
    // instructor of a session (they have no Zoom seat).
    const loadInstructors = async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get('/api/users', { params: { role: 'instructor', limit: 50 } }),
          api.get('/api/users', { params: { role: 'admin', limit: 20 } }),
        ]);
        // Backend's role=admin filter returns BOTH admins and superadmins
        // (single Mongo $in query) — drop the regular admins client-side.
        const superadmins = (r2.data.users || []).filter(u => u.role === 'superadmin');
        const all = [...(r1.data.users || []), ...superadmins];
        const unique = all.filter((u, i, arr) => arr.findIndex(a => a.id === u.id) === i);
        setInstructors(unique);
      } catch (e) { console.error('Failed to load instructors', e); }
    };
    loadInstructors();
    api.get('/api/sessions', { params: { batch_id: batchId, status: 'upcoming', limit: 200 } }).then(res => setExistingSessions(res.data.sessions));
  }, [batchId]);

  // Auto-select instructor: prefer batch default, but validate it exists in the current instructor list
  useEffect(() => {
    if (instructors.length === 0) return;
    const batchInstId = batch?.instructor_id;
    const validBatchInst = batchInstId && instructors.some(i => i.id === batchInstId);
    
    if (validBatchInst) {
      setSingleForm(prev => ({ ...prev, instructor_id: batchInstId }));
      setRecurForm(prev => ({ ...prev, instructor_id: batchInstId }));
    } else if (!singleForm.instructor_id || !instructors.some(i => i.id === singleForm.instructor_id)) {
      // Batch instructor doesn't exist — fallback to first available
      const firstId = instructors[0].id;
      setSingleForm(prev => ({ ...prev, instructor_id: firstId }));
      setRecurForm(prev => ({ ...prev, instructor_id: firstId }));
    }
  }, [instructors, batch]);

  // Fetch instructor's calendar when instructor or week changes
  useEffect(() => {
    const instId = singleForm.instructor_id;
    if (!instId) return;
    const start = toISTDateStr(calendarWeekStart);
    const end = toISTDateStr(new Date(calendarWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000));
    api.get('/api/calendar', { params: { date_from: start, date_to: end, instructor_id: instId } })
      .then(res => setCalendarSessions(res.data.sessions || []));
  }, [singleForm.instructor_id, calendarWeekStart]);

  const createSingleSession = async () => {
    // HARD VALIDATION — no exceptions
    if (!singleForm.instructor_id || singleForm.instructor_id === '') {
      toast.error('Please select an instructor');
      return;
    }
    if (!singleForm.scheduled_date) {
      toast.error('Please select a date');
      return;
    }
    if (singleForm.scheduled_date < today) {
      toast.error('Cannot create sessions in the past');
      return;
    }
    if (!singleForm.scheduled_time) {
      toast.error('Please select a time');
      return;
    }
    await postSingle(false);
  };

  const postSingle = async (overrideAvail) => {
    setLoading(true);
    try {
      const params = overrideAvail ? { override_availability: true } : {};
      await api.post('/api/sessions', { ...singleForm, batch_id: batchId }, { params });
      toast.success('Session created!');
      navigate(`/admin/batches/${batchId}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && typeof detail === 'string' && detail.includes('not available')) {
        // Availability conflict — offer override
        if (window.confirm(`${detail}\n\nOverride and schedule anyway?`)) {
          await postSingle(true);
          return;
        }
      } else if (Array.isArray(detail)) {
        const msg = detail[0]?.msg?.replace('Value error, ', '') || 'Validation error';
        toast.error(msg);
      } else if (typeof detail === 'object' && detail?.message) {
        toast.error(detail.message);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create session');
      }
    }
    setLoading(false);
  };

  const createRecurringSessions = async () => {
    const instId = recurForm.instructor_id || singleForm.instructor_id;
    if (!recurForm.start_date || !instId || (recurForm.repeat_unit === 'week' && recurForm.days_of_week.length === 0)) {
      toast.error('Please fill all required fields and select at least one day');
      return;
    }
    await postRecurring(false, instId);
  };

  const postRecurring = async (overrideAvail, instId) => {
    setLoading(true);
    try {
      const params = overrideAvail ? { override_availability: true } : {};
      const res = await api.post('/api/sessions/recurring', { ...recurForm, instructor_id: instId, batch_id: batchId }, { params });
      const created = res.data.count || 0;
      const skipped = res.data.skipped_count || 0;
      if (skipped > 0) {
        toast.success(`${created} session${created === 1 ? '' : 's'} created. ${skipped} skipped (already had an overlapping session).`);
      } else {
        toast.success(`${created} sessions created!`);
      }
      setRecurringOpen(false);
      navigate(`/admin/batches/${batchId}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && typeof detail === 'string' && detail.includes('not available')) {
        if (window.confirm(`${detail}\n\nOverride and schedule anyway?`)) {
          await postRecurring(true, instId);
          return;
        }
      } else if (Array.isArray(detail)) {
        const msg = detail[0]?.msg?.replace('Value error, ', '') || 'Validation error';
        toast.error(msg);
      } else if (typeof detail === 'object' && detail?.message) {
        toast.error(detail.message);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create sessions');
      }
    }
    setLoading(false);
  };

  const toggleDay = (day) => {
    setRecurForm(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/batches/${batchId}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <p className="text-xs text-muted-foreground">{batch?.name}</p>
          <h1 className="font-heading text-2xl font-semibold">Schedule Sessions</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Form */}
        <div className="space-y-4">
          <Card className="rounded-2xl">
            <CardContent className="pt-5 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Session Name</label>
                <Input value={singleForm.title} onChange={e => setSingleForm({...singleForm, title: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Instructor {!singleForm.instructor_id && <span className="text-destructive text-xs">(Required)</span>}</label>
                <select
                  value={singleForm.instructor_id}
                  onChange={e => setSingleForm({...singleForm, instructor_id: e.target.value})}
                  className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  required
                >
                  {instructors.map(i => <option key={i.id} value={i.id}>{i.name} ({i.role})</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Duration</label>
                <Select value={String(singleForm.duration_minutes)} onValueChange={v => setSingleForm({...singleForm, duration_minutes: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1hr 30min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Date</label>
                  <Input type="date" min={today} value={singleForm.scheduled_date} onChange={e => setSingleForm({...singleForm, scheduled_date: e.target.value})} data-testid="session-date-input" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Time <span className="text-xs text-muted-foreground font-normal">(IST)</span></label>
                  <Input type="time" value={singleForm.scheduled_time} onChange={e => setSingleForm({...singleForm, scheduled_time: e.target.value})} data-testid="session-time-input" />
                </div>
              </div>

              <div className="flex gap-2">
                <Button className="flex-1" onClick={createSingleSession} disabled={loading || !singleForm.instructor_id || !singleForm.scheduled_date || singleForm.scheduled_date < today} data-testid="add-session-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Add Session
                </Button>
                <Button variant="outline" onClick={() => setRecurringOpen(true)} data-testid="recurring-button">
                  <Repeat className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Instructor Calendar */}
        <div className="lg:col-span-2">
          <Card className="rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-3 border-b">
                <Button variant="ghost" size="icon" onClick={() => { const d = new Date(calendarWeekStart); d.setDate(d.getDate() - 7); setCalendarWeekStart(d); }}>&lt;</Button>
                <span className="text-sm font-medium">
                  {calendarWeekStart.toLocaleDateString('en', { month: 'short', day: 'numeric' })} - {new Date(calendarWeekStart.getTime() + 6 * 86400000).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <Button variant="ghost" size="icon" onClick={() => { const d = new Date(calendarWeekStart); d.setDate(d.getDate() + 7); setCalendarWeekStart(d); }}>&gt;</Button>
              </div>
              {(() => {
                const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(calendarWeekStart); d.setDate(calendarWeekStart.getDate() + i); return d; });
                const hours = Array.from({ length: 24 }, (_, i) => i);
                return (
                  <>
                    <div className="grid grid-cols-8 border-b">
                      <div className="p-1 border-r text-[10px] text-muted-foreground text-center">IST</div>
                      {weekDays.map((d, i) => {
                        const isToday = d.toDateString() === new Date().toDateString();
                        return (
                          <div key={i} className={`p-1 text-center border-r last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}>
                            <p className="text-[10px] text-muted-foreground">{d.toLocaleDateString('en', { weekday: 'short' })}</p>
                            <p className={`text-sm font-semibold ${isToday ? 'bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center mx-auto' : ''}`}>{d.getDate()}</p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="max-h-[450px] overflow-y-auto scrollbar-thin">
                      {hours.map(hour => (
                        <div key={hour} className="grid grid-cols-8 border-b min-h-[40px]">
                          <div className="p-0.5 text-[10px] text-muted-foreground border-r flex items-start justify-end pr-1 pt-0.5">
                            {hour > 12 ? `${hour - 12}pm` : hour === 12 ? '12pm' : `${hour}am`}
                          </div>
                          {weekDays.map((d, i) => {
                            const dateStr = toISTDateStr(d);
                            const daySessions = calendarSessions.filter(s => s.scheduled_date === dateStr && parseInt((s.scheduled_time || '').split(':')[0]) === hour);
                            return (
                              <div key={i} className="border-r last:border-r-0 p-0.5">
                                {daySessions.map(s => (
                                  <div key={s.id} className="rounded border px-1 py-0.5 text-[10px] bg-teal-50 border-teal-200 mb-0.5 truncate">
                                    <p className="font-medium truncate">{s.batch_name || s.title}</p>
                                    <p className="text-muted-foreground">{formatTime(s.scheduled_time)}</p>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
              {!singleForm.instructor_id && (
                <div className="p-6 text-center text-muted-foreground text-sm">Select an instructor to see their calendar</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recurring Session Modal */}
      <Dialog open={recurringOpen} onOpenChange={setRecurringOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="recurring-session-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Recurring Sessions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Instructor selector in recurring modal */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Instructor</label>
              <select
                value={recurForm.instructor_id || singleForm.instructor_id}
                onChange={e => setRecurForm({...recurForm, instructor_id: e.target.value})}
                className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {instructors.map(i => <option key={i.id} value={i.id}>{i.name} ({i.role})</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium">Repeats every</label>
              <Input type="number" min={1} max={4} className="w-16" value={recurForm.repeat_every} onChange={e => setRecurForm({...recurForm, repeat_every: parseInt(e.target.value) || 1})} />
              <Select value={recurForm.repeat_unit} onValueChange={v => setRecurForm({...recurForm, repeat_unit: v})}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="day">Day</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {recurForm.repeat_unit === 'week' && (
              <div>
                <label className="text-sm font-medium mb-2 block">On these days</label>
                <div className="flex gap-2">
                  {DAYS.map(d => (
                    <button key={d} onClick={() => toggleDay(d)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors duration-200 ${recurForm.days_of_week.includes(d) ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                      data-testid={`day-${DAY_LABELS[d].toLowerCase()}`}>
                      {DAY_LABELS[d]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Starts on</label>
                <Input type="date" min={today} value={recurForm.start_date} onChange={e => setRecurForm({...recurForm, start_date: e.target.value})} data-testid="recurring-start-date" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Time <span className="text-xs text-muted-foreground font-normal">(IST)</span></label>
                <Input type="time" value={recurForm.start_time} onChange={e => setRecurForm({...recurForm, start_time: e.target.value})} />
              </div>
              {/* V13.14.6 — duration field was missing; defaulted silently to 60 min */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Duration</label>
                <Select value={String(recurForm.duration_minutes)} onValueChange={(v) => setRecurForm({...recurForm, duration_minutes: parseInt(v, 10)})}>
                  <SelectTrigger data-testid="recurring-duration"><SelectValue /></SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="z-[200]">
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">60 min</SelectItem>
                    <SelectItem value="75">75 min</SelectItem>
                    <SelectItem value="90">90 min</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Ends</label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" checked={recurForm.end_type === 'date'} onChange={() => setRecurForm({...recurForm, end_type: 'date'})} className="w-4 h-4" />
                  <span className="text-sm">On</span>
                  <Input type="date" value={recurForm.end_date} onChange={e => setRecurForm({...recurForm, end_date: e.target.value})} disabled={recurForm.end_type !== 'date'} className="flex-1" />
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" checked={recurForm.end_type === 'occurrences'} onChange={() => setRecurForm({...recurForm, end_type: 'occurrences'})} className="w-4 h-4" />
                  <span className="text-sm">After</span>
                  <Input type="number" min={1} max={200} className="w-20" value={recurForm.occurrences} onChange={e => setRecurForm({...recurForm, occurrences: parseInt(e.target.value) || 1})} disabled={recurForm.end_type !== 'occurrences'} />
                  <span className="text-sm text-muted-foreground">occurrences</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecurringOpen(false)}>Cancel</Button>
            <Button onClick={createRecurringSessions} disabled={loading} data-testid="recurring-session-save-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
